import React, {ReactElement} from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardMedia
} from '@customink/pigment-react';
import {CardActionArea, Skeleton} from '@mui/material';
import {Product} from '../../models/product';
import services from '../../api/remoteServices';
import './index.scss';
import CheckCircle from '../../assets/icons/check_circle.svg';
import Sizes from '../../assets/icons/sizes.svg';
import Colors from '../../assets/icons/colors.svg';
import {Design} from '../../models/design';
import {useGetPriceQuery} from '../../api/storesApi';
import {formatCurrency} from '../../utils/helpers';

type ProductCardProps = {
  product: Product;
  productName: string;
  setProduct: (product: Product) => void;
  selected: boolean;
  design: Design;
};

const getSizeRange = (product: Product) => {
  if (product.sizes.length === 1) {
    return product.sizes[0];
  }
  return `${product.sizes[0]}-${product.sizes[product.sizes.length - 1]}`;
};

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  productName,
  setProduct,
  selected,
  design
}: ProductCardProps): ReactElement => {
  const priceResponse = useGetPriceQuery({
    productId: String(product.preferred_quote_color_ids.singles),
    frontColorCount: design.views.find(v => v.name === 'front')?.colorCount,
    backColorCount: design.views.find(v => v.name === 'back')?.colorCount
  });
  return (
    <Card
      className={`productCard ${selected ? 'selected' : ''}`}
      onClick={() => setProduct(product)}
      onKeyDown={() => setProduct(product)}
      role="radio"
      tabIndex={0}
      aria-checked="false">
      <CardActionArea className="productCardActionArea" disableRipple>
        <CardMedia
          component="img"
          image={`${services.mmsImages}/catalog/styles/${product.id}/catalog_detail_image.jpg`}
          alt="clothing product"
        />
        {selected && (
          <img className="selectedBadge" src={CheckCircle} alt="Selected" />
        )}
        <CardHeader className="productCardHeader" title={productName} />
        <CardContent className="productCardInfoSection">
          {priceResponse.data && (
            <CardHeader
              className="productPriceHeader"
              title={`${formatCurrency(Number(priceResponse.data))}`}
            />
          )}
          {!priceResponse.data && (
            <Skeleton className="productPriceHeaderSkeleton">
              <CardHeader title="$00.00" />
            </Skeleton>
          )}
          <div className="productCardInfoLine">
            <img src={Sizes} className="icon" alt="Sizes" />
            Sizes: {getSizeRange(product)}
          </div>
          <div className="productCardInfoLine">
            <img src={Colors} className="icon" alt="Colors" />
            Colors:{' '}
            {product.colors?.reduce(
              (n, e) => (e.singles_enabled ? n + 1 : n),
              0
            )}{' '}
            options
          </div>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default ProductCard;
